import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useAuth } from "AuthContext";
import { FACTORY_SYMBOL_CODES as SYMBOL_CODES } from "utils/symbols";
import { fmtDateTime } from "utils/datetime";
import { COLORS } from "theme/tokens";

const RISK_LABELS = { LOW: "低風險(均值)", MID: "中風險(趨勢)", HIGH: "高風險(突破)" };

// 池子(audience) 標籤/顏色：原本 chip 寫死成「INTERNAL→內部 / 其他→外部」，
//   害 RPO_RECENT 被誤顯示成「外部」（admin 數「活躍+外部」會多算到 RPO）。改成三態各自區分。
const AUDIENCE_LABELS = { EXTERNAL: "🚀 外部", INTERNAL: "🏦 內部", RPO_RECENT: "🔬 RPO" };
const AUDIENCE_LABELS_FULL = { EXTERNAL: "🚀 外部客戶", INTERNAL: "🏦 內部自營", RPO_RECENT: "🔬 RPO 研究池" };
const AUDIENCE_COLORS = { EXTERNAL: "#f44336", INTERNAL: "#1a237e", RPO_RECENT: "#00897b" };
// 2026-08-25 補 D1_FREQ：漏了它會讓 29 支日線菁英在列表上顯示成原始字串 "D1_FREQ"
const FREQ_LABELS = { D1_FREQ: "日線(D1)", HIGH_FREQ: "高頻(M15)", LOW_FREQ: "低頻(H1)", MID_FREQ: "中頻(H4)", M5_FREQ: "高頻(M5)", M1_FREQ: "高頻(M1)" };

// 自訂時段回測：每支策略的結果分類 → 標籤/顏色
const RB_OUTCOME = {
  PASS:      { label: "✅ 正常",    bg: COLORS.safe },
  FAIL:      { label: "❌ 不正常",  bg: COLORS.error },
  NO_TRADES: { label: "➖ 無交易",  bg: COLORS.grayDark },
  LEGACY:    { label: "⚪ Legacy",  bg: "#78909c" },
  ERROR:     { label: "⚠️ 回測錯誤", bg: "#fb8c00" },
};

function BankElitePage() {
  const { token } = useAuth();
  const [elites, setElites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedElite, setSelectedElite] = useState(null);
  const [codeView, setCodeView] = useState("live");  // 'live' | 'backtest' — 預設顯示 LIVE 版本（若有）
  const [converting, setConverting] = useState(false);
  const [filter, setFilter] = useState({ audience: "", symbol: "", riskLevel: "", frequency: "" });

  // 開啟某個 elite 詳情：預設顯示 LIVE（沒 LIVE 才退回 backtest）
  const openElite = (e) => {
    setSelectedElite(e);
    setCodeView(e.liveCode ? "live" : "backtest");
  };

  // 重新轉換 LIVE
  const handleReconvertLive = async () => {
    if (!selectedElite) return;
    setConverting(true);
    try {
      const r = await fetch(`/api/admin/bank-factory/elites/${selectedElite.id}/convert-and-save?force=1`, {
        method: "POST", headers: headers()
      });
      const d = await r.json();
      if (d.ok && d.status === "converted") {
        // 重新拉這個 elite 的完整資料
        const r2 = await fetch(`/api/admin/bank-factory/elites/${selectedElite.id}/live-code`, { headers: headers() });
        const updated = await r2.json();
        // 更新 selectedElite 和列表中的 elite
        const merged = { ...selectedElite, liveCode: updated.liveCode, liveConvertedAt: updated.liveConvertedAt, liveConvertReport: updated.liveConvertReport };
        setSelectedElite(merged);
        setElites(prev => prev.map(x => x.id === merged.id ? { ...x, ...merged } : x));
        setCodeView("live");
      } else {
        alert(`轉換${d.ok ? "完成（但被跳過）" : "失敗"}：${d.reason || "見 console"}`);
      }
    } catch (e) {
      alert("轉換錯誤：" + e.message);
    } finally {
      setConverting(false);
    }
  };

  // ── 生存審核相關 ──
  const [auditJobs, setAuditJobs] = useState([]);
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditDays, setAuditDays] = useState(30);
  // 預演：走完全相同的判定邏輯，但不改任何策略狀態（先看清楚會砍掉誰再決定）
  const [auditDryRun, setAuditDryRun] = useState(false);

  // ── 📊 自訂時段回測（唯讀診斷 + 報表）相關 ──
  const [uuidQuery, setUuidQuery] = useState("");   // 列表 UUID 快速篩選（client-side 即時）
  const [rbFrom, setRbFrom] = useState(() => new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10));
  const [rbTo, setRbTo]     = useState(() => new Date(Date.now() - 86400000).toISOString().slice(0, 10));
  const [rbJob, setRbJob]   = useState(null);       // 當前/最近一筆回測任務（含 results）
  const [rbBusy, setRbBusy] = useState(false);      // 啟動中

  // ── 📦 EXE 打包狀態相關 ──
  const [exeBuildMap, setExeBuildMap] = useState({});  // eliteId → exeBuild info
  const [exeBuildBusy, setExeBuildBusy] = useState(false);   // 批次按鈕 / 個別按鈕 disabled 旗標
  const [exeBuildSnackbar, setExeBuildSnackbar] = useState(null);

  // ── 🚀 LIVE 批次補齊相關 ──
  const [liveBusy, setLiveBusy] = useState(false);
  const [liveSnackbar, setLiveSnackbar] = useState(null);

  const headers = useCallback(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  // 拉所有 elite 的 EXE 打包狀態（merge 進 exeBuildMap）
  const refreshExeBuildStatus = useCallback(async () => {
    try {
      const r = await fetch("/api/admin/bank-factory/elites/build-status", { headers: headers() });
      const d = await r.json();
      if (d?.items) {
        const m = {};
        for (const it of d.items) m[it.id] = it;
        setExeBuildMap(m);
      }
    } catch (e) {
      console.warn("refreshExeBuildStatus failed:", e.message);
    }
  }, [headers]);

  // 觸發單一 elite 打包
  const handleBuildOneExe = async (eliteId) => {
    setExeBuildBusy(true);
    try {
      const r = await fetch(`/api/admin/bank-factory/elites/${eliteId}/build-exe`, {
        method: "POST", headers: headers()
      });
      const d = await r.json();
      setExeBuildSnackbar(d.ok ? `已排入打包：${eliteId.slice(0,8)}` : `失敗：${d.error || d.status}`);
      setTimeout(refreshExeBuildStatus, 1000);
    } catch (e) {
      setExeBuildSnackbar("錯誤：" + e.message);
    } finally {
      setExeBuildBusy(false);
    }
  };

  // 🚀 批次補齊 LIVE 版本（未轉換 + 失敗 + 舊版）
  const handleConvertAllMissingLive = async () => {
    if (!window.confirm("批次補齊所有「未轉 / 失敗 / 舊版本」的 LIVE 版本？\n（不影響已成功轉換的當前版本）")) return;
    setLiveBusy(true);
    try {
      const r = await fetch("/api/admin/bank-factory/elites/convert-all-missing", {
        method: "POST", headers: headers(),
        body: JSON.stringify({ includeOutdated: true, includeFailed: false })
      });
      const d = await r.json();
      setLiveSnackbar(`處理 ${d.targetCount} 個 → 成功 ${d.converted}，失敗 ${d.failed}`);
      // 重新拉 elite 列表 + 觸發 exe 狀態重拉（LIVE 轉成功會自動觸發 exe build）
      await fetchElites();
      refreshExeBuildStatus();
    } catch (e) {
      setLiveSnackbar("錯誤：" + e.message);
    } finally {
      setLiveBusy(false);
    }
  };

  // 批次補打（只跑 NEVER / FAILED / 舊版本，DONE 跳過）
  const handleBuildAllMissing = async () => {
    if (!window.confirm("批次補打所有「未打包 / 失敗 / 舊版本」的 elite，會背景跑數分鐘。確定？")) return;
    setExeBuildBusy(true);
    try {
      const r = await fetch("/api/admin/bank-factory/elites/build-all-missing", {
        method: "POST", headers: headers(),
        body: JSON.stringify({ includeFailed: true, includeOutdated: true })
      });
      const d = await r.json();
      setExeBuildSnackbar(`已排入 ${d.queued} 個打包，${d.skipped} 個跳過`);
      setTimeout(refreshExeBuildStatus, 1000);
    } catch (e) {
      setExeBuildSnackbar("錯誤：" + e.message);
    } finally {
      setExeBuildBusy(false);
    }
  };

  // 🔥 強制重新打包全部（包含已 DONE 的）— 測試環境 / liveTransformer 升級時用
  const handleRebuildAll = async () => {
    const confirmMsg =
      "⚠️ 強制重新打包所有 ACTIVE 用戶可見策略（包含已 DONE 的！）\n\n" +
      "這會：\n" +
      "  1. Reset 所有現有 DONE 紀錄 → NEVER（exePath 等清空）\n" +
      "  2. 重新排進 Bull queue 從頭打包\n\n" +
      "預估時間：每隻 ~5 分鐘，數十隻就要好幾小時。\n" +
      "用戶在這段期間下載 EXE 會失敗（exePath 暫時 null）。\n\n" +
      "確定要繼續嗎？";
    if (!window.confirm(confirmMsg)) return;
    setExeBuildBusy(true);
    try {
      const r = await fetch("/api/admin/bank-factory/elites/rebuild-all", {
        method: "POST", headers: headers(),
      });
      const d = await r.json();
      setExeBuildSnackbar(
        `🔥 強制重打：reset ${d.reset} 個、排進 ${d.queued} 個，預估 ${d.eta_minutes} 分鐘 (~${(d.eta_minutes / 60).toFixed(1)} 小時)`
      );
      setTimeout(refreshExeBuildStatus, 1000);
    } catch (e) {
      setExeBuildSnackbar("錯誤：" + e.message);
    } finally {
      setExeBuildBusy(false);
    }
  };

  // 拉 EXE 狀態 + 自動輪詢（有 RUNNING/PENDING 時 5 秒重拉）
  useEffect(() => {
    if (!token) return;
    refreshExeBuildStatus();
  }, [token, refreshExeBuildStatus]);
  useEffect(() => {
    const hasActive = Object.values(exeBuildMap).some(i => i.exeStatus === "RUNNING" || i.exeStatus === "PENDING");
    if (!hasActive) return;
    const id = setInterval(refreshExeBuildStatus, 5000);
    return () => clearInterval(id);
  }, [exeBuildMap, refreshExeBuildStatus]);

  // ── 行內動作：下載 EXE / Archive / Restore（2026-05-20 後端 commit 49eb946 + 54a1dfc）──

  // 📥 下載 EXE — 用 fetch + blob 保留 Authorization header（admin 也走 Bearer token）
  //    不能用 window.open / window.location，會丟失 Authorization → 401
  const handleDownloadExe = async (elite) => {
    try {
      const res = await fetch(`/api/admin/bank-factory/elites/${elite.id}/download`, {
        headers: headers(),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        alert(`下載失敗：${errJson.error || res.status}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Admin 友善檔名：symbol_frequency_risk_id8 (跟 server endpoint 邏輯一致)
      // 例：USDJPY_LOW_FREQ_LOW_4054bdef.exe
      a.download = `${elite.symbol}_${elite.frequency}_${elite.riskLevel}_${elite.id.slice(0, 8)}.exe`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('downloadExe failed', err);
      alert(`下載發生錯誤：${err.message}`);
    }
  };

  // 🗂️ 手動封存（ACTIVE → ARCHIVED）— 帶可選原因
  const handleArchive = async (elite) => {
    const reason = window.prompt(`封存 ${elite.symbol} ${elite.id.slice(0, 8)}？\n（封存原因，可選）:`, '管理員手動下架');
    if (reason === null) return; // 用戶按取消
    try {
      const res = await fetch(`/api/admin/bank-factory/elites/${elite.id}/archive`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ reason: reason || '管理員手動下架' }),
      });
      const json = await res.json();
      if (json.ok) {
        alert(`已封存：${json.message || elite.id.slice(0, 8)}`);
        fetchElites();
      } else {
        alert(`封存失敗：${json.error || '未知錯誤'}`);
      }
    } catch (err) {
      console.error('archive failed', err);
      alert(`封存發生錯誤：${err.message}`);
    }
  };

  // ♻️ Restore（ARCHIVED / INACTIVE → ACTIVE）
  const handleRestore = async (elite) => {
    if (!window.confirm(`恢復 ${elite.symbol} ${elite.id.slice(0, 8)} 為 ACTIVE？`)) return;
    try {
      const res = await fetch(`/api/admin/bank-factory/elites/${elite.id}/restore`, {
        method: 'POST',
        headers: headers(),
      });
      const json = await res.json();
      if (json.ok) {
        alert(`已恢復 ACTIVE：${elite.id.slice(0, 8)}`);
        fetchElites();
      } else {
        alert(`恢復失敗：${json.error || '未知錯誤'}`);
      }
    } catch (err) {
      console.error('restore failed', err);
      alert(`恢復發生錯誤：${err.message}`);
    }
  };

  const fetchElites = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams(filter).toString();
      const res = await fetch(`/api/admin/bank-factory/elites?${query}`, { headers: headers() });
      const data = await res.json();
      setElites(data);
    } catch (err) {
      console.error("Failed to fetch elites", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditJobs = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/bank-factory/audit-vault", { headers: headers() });
      const data = await res.json();
      setAuditJobs(data);

      const running = data.find(j => j.status === "RUNNING");
      if (running) {
        setIsAuditing(true);
      } else {
        setIsAuditing(false);
        // 如果剛完成，刷一下列表
        if (isAuditing) fetchElites();
      }
    } catch { }
  }, [headers, isAuditing]);

  useEffect(() => {
    if (token) {
      fetchElites();
      fetchAuditJobs();
    }
  }, [token, filter]);

  // 輪詢審核進度
  useEffect(() => {
    if (!isAuditing) return;
    const timer = setInterval(fetchAuditJobs, 3000);
    return () => clearInterval(timer);
  }, [isAuditing, fetchAuditJobs]);

  // ── 📊 自訂時段回測：拉任務 + 輪詢 ──
  const fetchRbJob = useCallback(async (jobId) => {
    try {
      const res = await fetch(`/api/admin/bank-factory/elites/range-backtest/${jobId}`, { headers: headers() });
      if (!res.ok) return;
      setRbJob(await res.json());
    } catch { }
  }, [headers]);

  // 進頁載入最近一筆回測任務（refresh 後可續看 / 續輪詢）
  const fetchRbLatest = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/bank-factory/elites/range-backtest", { headers: headers() });
      const list = await res.json();
      if (Array.isArray(list) && list[0]?.jobId) fetchRbJob(list[0].jobId);
    } catch { }
  }, [headers, fetchRbJob]);
  useEffect(() => { if (token) fetchRbLatest(); }, [token, fetchRbLatest]);

  // RUNNING 時每 3 秒輪詢進度
  useEffect(() => {
    if (!rbJob?.jobId || rbJob.status !== "RUNNING") return;
    const timer = setInterval(() => fetchRbJob(rbJob.jobId), 3000);
    return () => clearInterval(timer);
  }, [rbJob?.jobId, rbJob?.status, fetchRbJob]);


  const handleDelete = async (id) => {
    if (!window.confirm("確定要從精英庫中刪除此策略嗎？")) return;
    try {
      await fetch(`/api/admin/bank-factory/elites/${id}`, {
        method: "DELETE",
        headers: headers(),
      });
      fetchElites();
    } catch (err) {
      console.error("Delete failed", err);
      alert("刪除失敗：" + err.message);
    }
  };

  const columns = [
    {
      Header: "狀態", accessor: "status", width: "8%", Cell: ({ value }) => (
        <Chip
          label={value === "ACTIVE" ? "活躍" : "休眠"}
          size="small"
          sx={{
            bgcolor: value === "ACTIVE" ? COLORS.safe : COLORS.grayDark,
            color: COLORS.white, fontWeight: "bold"
          }}
        />
      )
    },
    {
      Header: "池子", accessor: "audience", width: "8%", Cell: ({ value }) => (
        <Chip
          label={AUDIENCE_LABELS[value] || value}
          size="small"
          sx={{
            bgcolor: AUDIENCE_COLORS[value] || "#757575",
            color: COLORS.white, fontWeight: "bold", fontSize: "0.7rem"
          }}
        />
      )
    },
    { Header: "商品", accessor: "symbol", width: "8%" },
    { Header: "風險", accessor: "riskLevel", Cell: ({ value }) => RISK_LABELS[value] || value },
    { Header: "頻率", accessor: "frequency", Cell: ({ value }) => FREQ_LABELS[value] || value },
    {
      // 這支策略是「什麼時候進到菁英庫」的（DB 存 UTC，fmtDateTime 依觀看者時區顯示）
      Header: "入庫時間", accessor: "createdAt", width: "10%",
      Cell: ({ value }) => value
        ? <MDTypography variant="caption" sx={{ whiteSpace: "nowrap" }}>{fmtDateTime(value)}</MDTypography>
        : "—"
    },
    {
      Header: "年化", accessor: "returnRatePct", Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="bold" color={value > 0 ? "success" : "error"}>
          {Number(value).toFixed(2)}%
        </MDTypography>
      )
    },
    { Header: "回撤", accessor: "maxDrawdownPct", Cell: ({ value }) => `${Number(value).toFixed(2)}%` },
    { Header: "PF", accessor: "profitFactor", Cell: ({ value }) => Number(value).toFixed(2) },
    { Header: "最後審核", accessor: "lastAuditAt", Cell: ({ value }) => value ? fmtDateTime(value) : "從未" },
    {
      Header: "LIVE", accessor: "liveConvertedAt", width: "8%",
      Cell: ({ row }) => {
        const e = row.original;
        if (e.liveConvertedAt) {
          return <span style={{ background: "#e8f5e9", color: COLORS.safe, padding: "2px 6px", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700 }}>✅ Ready</span>;
        }
        if (e.liveConvertReport) {
          return <span style={{ background: "#ffebee", color: COLORS.error, padding: "2px 6px", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700 }} title={e.liveConvertReport.unclassified?.map(u => `L${u.line}: ${u.raw}`).join("\n") || ""}>❌ Failed</span>;
        }
        return <span style={{ background: COLORS.grayLightest, color: "#455a64", padding: "2px 6px", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700 }}>⚠️ Missing</span>;
      }
    },
    {
      Header: "EXE", accessor: "exeBuildStatus", width: "10%",
      Cell: ({ row }) => {
        const info = exeBuildMap[row.original.id];
        // fallback：polling 的 /elites/build-status 只 cover EXTERNAL audience，
        // RPO_RECENT / INTERNAL 的 elite 永遠不會進 exeBuildMap → 用 row 原始值
        const status = info?.exeStatus ?? row.original.exeBuildStatus ?? "NEVER";
        const exeBuiltAt = info?.exeBuiltAt ?? row.original.exeBuiltAt;
        const exeBuildError = info?.exeBuildError ?? row.original.exeBuildError;
        const isOutdated = info?.isOutdated;
        const badge = {
          DONE: isOutdated
            ? { txt: "🕒 舊版", bg: "#fff8e1", color: "#f57c00" }
            : { txt: "✅ Ready", bg: "#e8f5e9", color: COLORS.safe },
          RUNNING: { txt: "🔨 打包中", bg: "#e3f2fd", color: "#1565c0" },
          PENDING: { txt: "⏳ 排隊",  bg: "#f3e5f5", color: "#6a1b9a" },
          FAILED:  { txt: "❌ 失敗",  bg: "#ffebee", color: COLORS.error },
          NEVER:   { txt: "⚪ 未打",  bg: COLORS.grayLightest, color: "#455a64" },
        }[status] || { txt: status, bg: COLORS.grayLightest, color: COLORS.black };
        const canBuild = !!row.original.liveConvertedAt && status !== "RUNNING" && status !== "PENDING";
        return (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <span style={{ background: badge.bg, color: badge.color, padding: "2px 6px", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700 }}
                  title={exeBuildError || (exeBuiltAt ? `打包於 ${new Date(exeBuiltAt).toLocaleString()}` : "")}>
              {badge.txt}
            </span>
            {canBuild && (
              <MDButton
                size="small" variant="outlined" color={status === "FAILED" ? "error" : "warning"}
                disabled={exeBuildBusy}
                onClick={(e) => { e.stopPropagation(); handleBuildOneExe(row.original.id); }}
                sx={{ minWidth: 0, py: 0.25, px: 0.5, fontSize: "0.65rem" }}
              >
                {status === "DONE" ? "🔁" : "📦"}
              </MDButton>
            )}
          </MDBox>
        );
      }
    },
    {
      Header: "操作",
      accessor: "id",
      Cell: ({ row }) => {
        const e = row.original;
        // exeStatus 優先用 polling 過的 exeBuildMap，沒拿到才用 row 原始的 exeBuildStatus
        const exeStatus = exeBuildMap[e.id]?.exeStatus ?? e.exeBuildStatus;
        const canDownload = exeStatus === 'DONE' && !!e.exePath;
        const isActive = e.status === 'ACTIVE';
        const isArchived = e.status === 'ARCHIVED' || e.status === 'INACTIVE';
        return (
          <MDBox display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
            <MDButton size="small" color="info" onClick={() => openElite(e)}>
              詳情
            </MDButton>
            {/* 📥 下載 EXE（fetch+blob 保留 Authorization）*/}
            {canDownload && (
              <Tooltip arrow title="下載 EXE">
                <span>
                  <MDButton
                    iconOnly size="small" color="success"
                    onClick={(ev) => { ev.stopPropagation(); handleDownloadExe(e); }}
                  >
                    <Icon>download</Icon>
                  </MDButton>
                </span>
              </Tooltip>
            )}
            {/* 🗂️ 手動封存 */}
            {isActive && (
              <Tooltip arrow title="封存（下架）">
                <span>
                  <MDButton
                    iconOnly size="small" color="warning"
                    onClick={(ev) => { ev.stopPropagation(); handleArchive(e); }}
                  >
                    <Icon>inventory_2</Icon>
                  </MDButton>
                </span>
              </Tooltip>
            )}
            {/* ♻️ Restore */}
            {isArchived && (
              <Tooltip arrow title="恢復 ACTIVE">
                <span>
                  <MDButton
                    iconOnly size="small" color="info"
                    onClick={(ev) => { ev.stopPropagation(); handleRestore(e); }}
                  >
                    <Icon>restore</Icon>
                  </MDButton>
                </span>
              </Tooltip>
            )}
            <MDButton iconOnly size="small" color="error" onClick={() => handleDelete(e.id)}>
              <Icon>delete</Icon>
            </MDButton>
          </MDBox>
        );
      },
    },
  ];

  // 有審核正在跑就顯示那一筆（2026-09-30）：月審安全化之後，最新一筆可能是「沒有執行」的
  //   SKIPPED／EXPIRED，而真正在跑的是較早排入的那一筆——只看 [0] 會把進度條畫成 0%。
  //   沒有在跑的就照舊顯示最新一筆。auditJobs 在 API 回錯誤物件時不是陣列，所以先檢查。
  const latestAudit = (Array.isArray(auditJobs) ? auditJobs.find((j) => j && j.status === "RUNNING") : null) || auditJobs[0];
  const auditProgress = latestAudit && latestAudit.totalStrategies > 0
    ? Math.round((latestAudit.completedCount / latestAudit.totalStrategies) * 100)
    : 0;

  // ── 最新一筆審核該怎麼顯示（2026-09-30，月審安全化之後 status／reason 多了幾種）──
  //   status：RUNNING／DONE／FAILED，以及新的 EXPIRED（排入太久沒執行）、INTERRUPTED（中途被打斷）、
  //           SKIPPED（已有另一場正式審核在跑）。
  //   reason：預演以 "DRY_RUN" 開頭（後面可能接「｜EXPIRED：…」等說明，所以不能用 === 比）；
  //           正式審核跑完但整場沒改任何狀態時是 "HALTED_ENV_FAILURE：…"。
  //   原本只在 DONE 時顯示卡片、而且一律寫「✅ 審核任務已完成／休眠/淘汰 N」——
  //   沒執行的審核整張卡消失、整場沒動的審核看起來像真的下架了 N 支。
  const auditReason = String(latestAudit?.reason || "");
  const auditIsDry = auditReason.startsWith("DRY_RUN");
  const auditIsHalted = latestAudit?.status === "DONE" && auditReason.startsWith("HALTED");
  const AUDIT_STATUS_VIEW = {
    DONE: { label: "✅ 審核任務已完成", color: "success" },
    EXPIRED: { label: "⏰ 這場審核沒有執行（排入太久才輪到）", color: "warning" },
    INTERRUPTED: { label: "⚠️ 這場審核被中斷", color: "warning" },
    SKIPPED: { label: "✋ 這場審核沒有執行（已有另一場在跑）", color: "warning" },
    FAILED: { label: "❌ 審核失敗", color: "error" },
  };
  const auditView = isAuditing
    ? { label: "🔄 系統回測進度", color: "info" }
    : auditIsHalted
      ? { label: "🛑 審核跑完了，但整場未改任何狀態", color: "warning" }
      : (AUDIT_STATUS_VIEW[latestAudit?.status] || null);
  // 要不要顯示原因：預演的 "DRY_RUN" 本身不算原因（已經有「預演」標籤），其餘有內容就顯示
  const auditReasonText = auditReason.replace(/^DRY_RUN｜?/, "");
  // ── 被中斷／失敗的審核，到底有沒有改到策略狀態（2026-09-30 第三輪）──
  //   原本 INTERRUPTED／FAILED 一律當成「沒改到」→ 第三欄寫「判定失敗(未下架)」。但審核的狀態變更
  //   全部排在「每一支都判完」之後：在套用階段才被中斷的那一場，可能已經下架了一部分、信也寄了一部分。
  //   只用 job 列現有的欄位判斷（不加欄位、不多打 API）：
  //     · 完成數 < 總數（或總數是 0）→ 還在判定階段就停了 → 一支都還沒動。
  //     · 完成數 ≥ 總數，而且 errorSummary 第一筆是審核自己寫的總結（kind=RUN_SUMMARY）並標明整場沒套用
  //       （revoked：套用前發現這一列已被收回／讀不到；halted：環境異常保護；staleRun：跑太久）→ 確定沒動。
  //     · 完成數 ≥ 總數、又沒有上面那種總結 → 判斷不出來 → 如實寫「可能已部分套用，見說明」
  //       （說明＝卡片左邊顯示的 reason；worker 與開機對帳標 INTERRUPTED 時都會寫明停在哪個階段）。
  const auditRunSummary = Array.isArray(latestAudit?.errorSummary) && latestAudit.errorSummary[0]?.kind === "RUN_SUMMARY"
    ? latestAudit.errorSummary[0] : null;
  const auditSaidNoApply = !!auditRunSummary && (auditRunSummary.revoked === true || auditRunSummary.halted === true || auditRunSummary.staleRun === true);
  const auditJudgedAll = Number(latestAudit?.totalStrategies) > 0 && Number(latestAudit?.completedCount) >= Number(latestAudit?.totalStrategies);
  const auditMaybePartial = !!latestAudit && !isAuditing && !auditIsDry
    && (latestAudit.status === "INTERRUPTED" || latestAudit.status === "FAILED")
    && auditJudgedAll && !auditSaidNoApply;
  // 這場有沒有真的改到策略狀態：預演、整場沒動、沒執行的都沒有；「可能已部分套用」的不算在這裡
  const auditChangedNothing = auditIsDry || auditIsHalted || (latestAudit && !isAuditing && latestAudit.status !== "DONE" && !auditMaybePartial);
  // 第三欄（判定失敗的支數）底下那行字
  const auditRemovedLabel = auditMaybePartial ? "判定失敗(可能已部分套用，見說明)" : (auditChangedNothing ? "判定失敗(未下架)" : "休眠/淘汰");

  // 列表 UUID 快速篩選（client-side，逐字即時，不重打 API）
  const displayedElites = uuidQuery.trim()
    ? elites.filter((e) => (e.id || "").toLowerCase().includes(uuidQuery.trim().toLowerCase()))
    : elites;

  // 自訂時段回測：進度 / 狀態
  const rbProgress = rbJob && rbJob.totalStrategies > 0
    ? Math.round((rbJob.completedCount / rbJob.totalStrategies) * 100)
    : 0;
  const rbRunning = rbJob?.status === "RUNNING";

  // 回測結果表欄位
  const rbColumns = [
    {
      Header: "UUID", accessor: "eliteId", width: "15%",
      Cell: ({ value }) => (
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <Tooltip arrow title={value || ""}>
            <span style={{ fontFamily: "monospace", fontSize: "0.72rem" }}>{(value || "").slice(0, 8)}…</span>
          </Tooltip>
          <MDButton iconOnly size="small" color="secondary" variant="text"
            onClick={() => navigator.clipboard.writeText(value)}
            sx={{ minWidth: 0, p: 0.25 }}>
            <Icon sx={{ fontSize: "1rem !important" }}>content_copy</Icon>
          </MDButton>
        </MDBox>
      )
    },
    { Header: "商品", accessor: "symbol" },
    { Header: "風險", accessor: "riskLevel", Cell: ({ value }) => RISK_LABELS[value] || value },
    { Header: "頻率", accessor: "frequency", Cell: ({ value }) => FREQ_LABELS[value] || value },
    {
      Header: "結果", accessor: "outcome",
      Cell: ({ value }) => {
        const o = RB_OUTCOME[value] || { label: value, bg: "#bdbdbd" };
        return <Chip label={o.label} size="small" sx={{ bgcolor: o.bg, color: COLORS.white, fontWeight: "bold", fontSize: "0.7rem" }} />;
      }
    },
    {
      Header: "報酬%", accessor: "returnPct",
      Cell: ({ value }) => value == null ? "—" : (
        <MDTypography variant="caption" fontWeight="bold" color={value >= 0 ? "success" : "error"}>
          {Number(value).toFixed(2)}%
        </MDTypography>
      )
    },
    { Header: "MDD%", accessor: "mddPct", Cell: ({ value }) => value == null ? "—" : `${Number(value).toFixed(2)}%` },
    { Header: "PF", accessor: "profitFactor", Cell: ({ value }) => value == null ? "—" : Number(value).toFixed(2) },
    { Header: "勝率%", accessor: "winRate", Cell: ({ value }) => value == null ? "—" : `${Number(value).toFixed(1)}%` },
    { Header: "交易", accessor: "trades", Cell: ({ value }) => value == null ? "—" : value },
    {
      Header: "原因", accessor: "reason", width: "22%",
      Cell: ({ value }) => (
        <Tooltip arrow title={value || ""}>
          <span style={{ fontSize: "0.72rem", color: COLORS.grayDark }}>
            {value && value.length > 28 ? value.slice(0, 28) + "…" : (value || "")}
          </span>
        </Tooltip>
      )
    },
  ];

  const handleStartAudit = async () => {
    const days = Number(auditDays);
    if (!days || days < 7 || days > 365) { alert("審核天數需介於 7 ~ 365 天"); return; }
    // 後端 bankAuditService 依頻率調整審核窗口：
    //   D1: max(730, days*24)、M15: days、H1/MID: days*2、M5: max(14, days/2)、M1: max(7, days/4)
    const d1Days = Math.max(730, days * 24);
    const m5Days = Math.max(14, Math.floor(days / 2));
    const m1Days = Math.max(7, Math.floor(days / 4));
    if (!window.confirm(
      (auditDryRun
        ? `【預演】只判定、不改任何策略狀態。\n\n`
        : `⚠️ 這會真的下架策略：判定失效者 ACTIVE → INACTIVE，並寄信＋站內通知給訂閱者。\n` +
          `（INACTIVE 是可逆的休眠；日後表現回升可能被「覺醒」回 ACTIVE。）\n` +
          `下架（含相關性去重）與覺醒各有單次上限，超過的只判定、不改狀態；\n` +
          `上限由伺服器設定（AUDIT_RETIRE_CAP_RATIO／AUDIT_AWAKEN_CAP），不是固定的 40%。\n\n`) +
      `審核窗口依頻率調整：\n` +
      `  D1  (D1_FREQ):   ${d1Days} 天\n` +
      `  M15 (HIGH_FREQ): ${days} 天\n` +
      `  H1  (LOW_FREQ):  ${days * 2} 天\n` +
      `  H1  (MID_FREQ 舊): ${days * 2} 天\n` +
      `  M5  (M5_FREQ):   ${m5Days} 天\n` +
      `  M1  (M1_FREQ):   ${m1Days} 天\n\n` +
      `近期健檢過關者還要再過一次跨期 walk-forward 閘門（與晉升同標準），\n` +
      `因此耗時較長（全庫約 3~4 小時）。確定啟動嗎？`
    )) return;
    try {
      await axios.post("/api/admin/bank-factory/audit-vault", { days, dryRun: auditDryRun }, { headers: headers() });
      fetchAuditJobs();
    } catch (err) {
      console.error("啟動審核失敗", err);
      alert("啟動審核失敗：" + (err?.response?.data?.error || err.message));
    }
  };

  const handlePurifySkills = async () => {
    if (window.confirm("確定要執行技能庫大掃除嗎？這將合併相似邏輯的招式。")) {
      try {
        setIsAuditing(true);
        const res = await axios.post("/api/admin/bank-factory/purify-skills", {}, {
          headers: headers(),
          timeout: 600000 // 10 分鐘超時，給 AI 足夠時間萃取 47 個策略
        });
        alert(`✨ 精煉完成！共合併了 ${res.data.totalMerged} 個重複技能。`);
      } catch (err) {
        console.error("精煉失敗", err);
        alert("精煉失敗：" + (err?.response?.data?.error || err.message));
      } finally {
        setIsAuditing(false);
      }
    }
  };

  // 📊 啟動自訂時段回測（依目前篩選；唯讀，不改策略狀態）
  const handleStartRangeBacktest = async () => {
    if (!rbFrom || !rbTo) { alert("請選擇回測起訖日期"); return; }
    if (rbFrom >= rbTo) { alert("起始日需早於結束日"); return; }
    const n = displayedElites.length;
    if (n === 0) { alert("目前篩選沒有符合的策略"); return; }
    const filterPayload = { ...filter, id: uuidQuery.trim() };
    if (!window.confirm(
      `對「目前篩選」的 ${n} 支策略，用 ${rbFrom} ~ ${rbTo} 跑回測？\n\n` +
      `⚠️ 唯讀診斷，不會改動任何策略狀態。\n` +
      `每支最久 5 分鐘、逐支序列跑，${n} 支可能需要不少時間。`
    )) return;
    setRbBusy(true);
    try {
      const res = await fetch("/api/admin/bank-factory/elites/range-backtest", {
        method: "POST", headers: headers(),
        body: JSON.stringify({ fromDate: rbFrom, toDate: rbTo, filter: filterPayload }),
      });
      const d = await res.json();
      if (!res.ok || !d.ok) { alert("啟動失敗：" + (d.error || res.status)); return; }
      fetchRbJob(d.jobId);   // 立刻拉初始進度，輪詢 useEffect 接手
    } catch (e) {
      alert("啟動錯誤：" + e.message);
    } finally {
      setRbBusy(false);
    }
  };

  // 下載 CSV 報表（fetch+blob 保留 Authorization）
  const handleDownloadRbCsv = async () => {
    if (!rbJob?.jobId) return;
    try {
      const res = await fetch(`/api/admin/bank-factory/elites/range-backtest/${rbJob.jobId}/report.csv`, { headers: headers() });
      if (!res.ok) { alert("下載失敗：" + res.status); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `range_backtest_${rbJob.fromDate}_${rbJob.toDate}_${rbJob.jobId.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("下載錯誤：" + e.message);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>

          {/* ── 頂部監控中心：一體化設計 ── */}
          <Grid item xs={12}>
            <Card sx={{
              overflow: "hidden",
              boxShadow: ({ boxShadows: { xxl } }) => xxl,
              border: "1px solid #e0e0e0"
            }}>
              <MDBox
                p={3}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ bgcolor: COLORS.grayLightest, borderBottom: "1px solid #eee" }}
              >
                <MDBox>
                  <MDTypography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon sx={{ color: "#fb8c00" }}>security</Icon> 金庫生存監控中心
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    策略健康檢測與重複性清理 · 根據最新行情動態調整狀態並移除冗餘策略
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={2} alignItems="center">
                  <MDButton
                    variant="outlined"
                    color="info"
                    disabled={isAuditing}
                    onClick={handlePurifySkills}
                    sx={{ borderRadius: "20px" }}
                  >
                    ✨ 精煉技能庫
                  </MDButton>
                  <TextField
                    label="審核天數 (H1)"
                    type="number"
                    size="small"
                    value={auditDays}
                    onChange={(e) => setAuditDays(e.target.value)}
                    disabled={isAuditing}
                    inputProps={{ min: 7, max: 365 }}
                    sx={{ width: 130, "& .MuiInputBase-root": { bgcolor: COLORS.white, borderRadius: "10px" } }}
                  />
                  <Tooltip title="預演會跑完全相同的判定邏輯，但不改任何策略狀態。先看清楚會砍掉誰，再決定要不要真的執行。">
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={auditDryRun}
                          disabled={isAuditing}
                          onChange={(e) => setAuditDryRun(e.target.checked)}
                        />
                      }
                      label={
                        <MDTypography variant="caption" fontWeight="bold" color={auditDryRun ? "info" : "secondary"}>
                          預演（不改狀態）
                        </MDTypography>
                      }
                      sx={{ mr: 0 }}
                    />
                  </Tooltip>
                  <MDButton
                    variant="gradient"
                    color={auditDryRun ? "info" : "warning"}
                    disabled={isAuditing}
                    onClick={handleStartAudit}
                    sx={{ px: 4, borderRadius: "20px" }}
                  >
                    {isAuditing ? (
                      <><CircularProgress size={16} sx={{ mr: 1, color: COLORS.white }} /> 處理中...</>
                    ) : auditDryRun ? (
                      "🔍 預演生存審核 (只判定不下架)"
                    ) : (
                      "🕵️ 啟動金庫生存審核 (汰弱留強+去重)"
                    )}
                  </MDButton>
                </MDBox>
              </MDBox>

              {latestAudit && auditView && (
                <MDBox px={4} py={3} sx={{ bgcolor: COLORS.white }}>
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <MDTypography variant="caption" fontWeight="bold" color="secondary">
                            {auditView.label}
                          </MDTypography>
                          {/* 預演旗標編碼在 reason（零 migration）→ 別讓預演結果被誤讀成真的下架了 */}
                          {auditIsDry && (
                            <Chip label="預演 · 未改任何狀態" size="small" color="info" variant="outlined"
                              sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                          )}
                        </MDBox>
                        <MDTypography variant="h6" fontWeight="bold" color="info">{auditProgress}%</MDTypography>
                      </MDBox>
                      <LinearProgress
                        variant="determinate"
                        value={auditProgress}
                        sx={{ height: 12, borderRadius: 6, bgcolor: COLORS.grayLighter }}
                        color={auditView.color}
                      />
                      {/* 沒執行／被中斷／整場沒動／失敗：把後端寫在 reason 的原因直接顯示出來 */}
                      {!isAuditing && auditReasonText && (
                        <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {auditReasonText}
                        </MDTypography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <MDBox display="flex" justifyContent="space-around">
                        <MDBox sx={{ textAlign: "center" }}>
                          <MDTypography variant="h4" fontWeight="bold" color="dark">{latestAudit.totalStrategies}</MDTypography>
                          <MDTypography variant="caption" fontWeight="bold" color="text">策略總量</MDTypography>
                        </MDBox>
                        <MDBox sx={{ textAlign: "center" }}>
                          <MDTypography variant="h4" fontWeight="bold" color="success">{latestAudit.keepCount}</MDTypography>
                          <MDTypography variant="caption" fontWeight="bold" color="text">通過/保留</MDTypography>
                        </MDBox>
                        <MDBox sx={{ textAlign: "center" }}>
                          <MDTypography variant="h4" fontWeight="bold" color="error">{latestAudit.removedCount}</MDTypography>
                          <MDTypography variant="caption" fontWeight="bold" color="text">
                            {auditRemovedLabel}
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          sx={{ textAlign: "center" }}
                          title="elite.code 缺現代 LIVE 框架（舊版 Beast 生成），跳過審核不算成敗"
                        >
                          <MDTypography variant="h4" fontWeight="bold" color="secondary">{latestAudit.legacyCount || 0}</MDTypography>
                          <MDTypography variant="caption" fontWeight="bold" color="text">Legacy 跳過</MDTypography>
                        </MDBox>
                        <MDBox
                          sx={{ textAlign: "center", cursor: latestAudit.errorSummary?.length ? "help" : "default" }}
                          title={
                            latestAudit.errorSummary?.length
                              ? latestAudit.errorSummary.slice(0, 10).map(e => `${e.key}: ${e.msg}`).join("\n")
                                + (latestAudit.errorSummary.length > 10 ? `\n...另 ${latestAudit.errorSummary.length - 10} 筆` : "")
                              : undefined
                          }
                        >
                          <MDTypography variant="h4" fontWeight="bold" color="warning">{latestAudit.failedCount || 0}</MDTypography>
                          <MDTypography variant="caption" fontWeight="bold" color="text">回測失敗/判不出來</MDTypography>
                        </MDBox>
                        <MDBox sx={{ textAlign: "center", borderLeft: "1px solid #eee", pl: 4 }}>
                          <MDTypography variant="h6" fontWeight="bold" color="secondary">
                            {new Date(latestAudit.updatedAt).toLocaleTimeString()}
                          </MDTypography>
                          <MDTypography variant="caption" fontWeight="bold" color="text">最後更新</MDTypography>
                        </MDBox>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              )}
            </Card>
          </Grid>

          {/* ── 📊 自訂時段回測（唯讀診斷 + 報表）── */}
          <Grid item xs={12}>
            <Card sx={{ overflow: "hidden", boxShadow: ({ boxShadows: { xxl } }) => xxl, border: "1px solid #e0e0e0" }}>
              <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}
                sx={{ bgcolor: COLORS.grayLightest, borderBottom: "1px solid #eee" }}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon sx={{ color: "#1565c0" }}>insights</Icon> 自訂時段回測（唯讀診斷）
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    指定歷史區間，檢驗「目前篩選」的策略在該時段是否正常 · 不改動任何策略狀態 · 可下載 CSV 報表（含完整 UUID）
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <TextField label="起始日" type="date" size="small"
                    value={rbFrom} onChange={(e) => setRbFrom(e.target.value)}
                    disabled={rbRunning} InputLabelProps={{ shrink: true }}
                    sx={{ width: 160, "& .MuiInputBase-root": { bgcolor: COLORS.white, borderRadius: "10px" } }} />
                  <TextField label="結束日" type="date" size="small"
                    value={rbTo} onChange={(e) => setRbTo(e.target.value)}
                    disabled={rbRunning} InputLabelProps={{ shrink: true }}
                    sx={{ width: 160, "& .MuiInputBase-root": { bgcolor: COLORS.white, borderRadius: "10px" } }} />
                  <MDButton variant="gradient" color="info" disabled={rbRunning || rbBusy}
                    onClick={handleStartRangeBacktest} sx={{ px: 3, borderRadius: "20px" }}>
                    {rbRunning
                      ? <><CircularProgress size={16} sx={{ mr: 1, color: COLORS.white }} /> 回測中...</>
                      : `📊 回測目前篩選 (${displayedElites.length})`}
                  </MDButton>
                  {rbJob && rbJob.status === "DONE" && rbJob.results?.length > 0 && (
                    <MDButton variant="outlined" color="dark" onClick={handleDownloadRbCsv} sx={{ borderRadius: "20px" }}>
                      ⬇️ 下載 CSV
                    </MDButton>
                  )}
                </MDBox>
              </MDBox>

              {rbJob && (
                <MDBox px={4} py={3} sx={{ bgcolor: COLORS.white }}>
                  <MDBox display="flex" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
                    <MDTypography variant="caption" fontWeight="bold" color="secondary">
                      {rbRunning ? "🔄 回測進度" : rbJob.status === "FAILED" ? "❌ 回測任務失敗" : "✅ 回測任務完成"}
                      {" · "}{rbJob.fromDate} ~ {rbJob.toDate}
                    </MDTypography>
                    <MDTypography variant="h6" fontWeight="bold" color="info">
                      {rbJob.completedCount}/{rbJob.totalStrategies}（{rbProgress}%）
                    </MDTypography>
                  </MDBox>
                  <LinearProgress variant="determinate" value={rbProgress}
                    sx={{ height: 12, borderRadius: 6, bgcolor: COLORS.grayLighter }}
                    color={rbRunning ? "info" : (rbJob.status === "FAILED" ? "error" : "success")} />
                  {rbJob.status === "FAILED" && rbJob.reason && (
                    <MDTypography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                      {rbJob.reason}
                    </MDTypography>
                  )}

                  {/* 統計卡 */}
                  <MDBox display="flex" justifyContent="space-around" mt={3} flexWrap="wrap" gap={2}>
                    {[
                      { label: "策略總量", value: rbJob.totalStrategies, color: "dark" },
                      { label: "✅ 正常", value: rbJob.passCount, color: "success" },
                      { label: "❌ 不正常", value: rbJob.failCount, color: "error" },
                      { label: "➖ 無交易", value: rbJob.noTradeCount, color: "secondary" },
                      { label: "⚪ Legacy", value: rbJob.legacyCount, color: "secondary" },
                      { label: "⚠️ 錯誤", value: rbJob.errorCount, color: "warning" },
                    ].map((s) => (
                      <MDBox key={s.label} sx={{ textAlign: "center" }}>
                        <MDTypography variant="h4" fontWeight="bold" color={s.color}>{s.value ?? 0}</MDTypography>
                        <MDTypography variant="caption" fontWeight="bold" color="text">{s.label}</MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>

                  {/* 結果表（每支：UUID + 結果 + KPI + 原因）*/}
                  {rbJob.results?.length > 0 && (
                    <MDBox mt={2}>
                      <DataTable
                        table={{ columns: rbColumns, rows: rbJob.results }}
                        isSorted={true}
                        entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50, 100] }}
                        showTotalEntries={true}
                        noEndBorder
                      />
                    </MDBox>
                  )}
                </MDBox>
              )}
            </Card>
          </Grid>

          {/* ── 列表區域 ── */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2.5}
                px={3}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox>
                  <MDTypography variant="h6" color="white" fontWeight="bold">
                    Beast AI 精英策略庫
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                    共儲存 {elites.length} 個高效能策略{uuidQuery.trim() && displayedElites.length !== elites.length ? `（UUID 篩出 ${displayedElites.length}）` : ""}
                    {(() => {
                      // LIVE 轉換狀態
                      const live = { ready: 0, failed: 0, missing: 0 };
                      for (const e of elites) {
                        if (e.liveConvertedAt) live.ready++;
                        else if (e.liveConvertReport) live.failed++;
                        else live.missing++;
                      }
                      // EXE 打包狀態
                      const exe = { done: 0, building: 0, failed: 0, never: 0 };
                      for (const it of Object.values(exeBuildMap)) {
                        if (it.exeStatus === "DONE" && !it.isOutdated) exe.done++;
                        else if (it.exeStatus === "RUNNING" || it.exeStatus === "PENDING") exe.building++;
                        else if (it.exeStatus === "FAILED") exe.failed++;
                        else exe.never++;
                      }
                      return ` · LIVE: ${live.ready}✅ ${live.failed}❌ ${live.missing}⚠️ · EXE: ${exe.done}✅ ${exe.building}🔨 ${exe.failed}❌ ${exe.never}⚪`;
                    })()}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
                  {/* 🚀 批次補齊 LIVE 按鈕 */}
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={liveBusy}
                    onClick={handleConvertAllMissingLive}
                    sx={{
                      color: "white", borderColor: "rgba(255,255,255,0.6)",
                      "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.1)" },
                      fontWeight: 600, height: 40,
                    }}
                  >
                    🚀 批次補齊 LIVE
                  </Button>

                  {/* 📦 批次補打 EXE 按鈕（只跑 NEVER/FAILED/舊版本）*/}
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={exeBuildBusy}
                    onClick={handleBuildAllMissing}
                    sx={{
                      color: "white", borderColor: "rgba(255,255,255,0.6)",
                      "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.1)" },
                      fontWeight: 600, height: 40,
                    }}
                  >
                    📦 批次補打 EXE
                  </Button>

                  {/* 🔥 強制重打全部按鈕（包含已 DONE 的）*/}
                  <Button
                    variant="contained"
                    size="small"
                    disabled={exeBuildBusy}
                    onClick={handleRebuildAll}
                    sx={{
                      bgcolor: "#dc2626", color: "white",
                      "&:hover": { bgcolor: "#b91c1c" },
                      "&.Mui-disabled": { bgcolor: "rgba(220, 38, 38, 0.4)", color: "white" },
                      fontWeight: 600, height: 40,
                    }}
                  >
                    🔥 強制重打全部
                  </Button>

                  {/* 受眾篩選 */}
                  <FormControl size="small" sx={{
                    minWidth: 130,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                      "&:hover fieldset": { borderColor: "white" },
                    },
                    "& .MuiInputLabel-root": { color: "white" },
                    "& .MuiSvgIcon-root": { color: "white" }
                  }}>
                    <InputLabel sx={{ color: "white !important" }}>策略池</InputLabel>
                    <Select
                      value={filter.audience}
                      label="策略池"
                      onChange={(e) => setFilter({ ...filter, audience: e.target.value })}
                      sx={{ height: "40px" }}
                    >
                      <MenuItem value="">全部</MenuItem>
                      <MenuItem value="EXTERNAL">🚀 外部客戶</MenuItem>
                      <MenuItem value="INTERNAL">🏦 內部自營</MenuItem>
                      <MenuItem value="RPO_RECENT">🔬 RPO 研究池</MenuItem>
                    </Select>
                  </FormControl>

                  {/* 商品篩選 */}
                  <FormControl size="small" sx={{
                    minWidth: 120,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                      "&:hover fieldset": { borderColor: "white" },
                    },
                    "& .MuiInputLabel-root": { color: "white" },
                    "& .MuiSvgIcon-root": { color: "white" }
                  }}>
                    <InputLabel sx={{ color: "white !important" }}>商品</InputLabel>
                    <Select
                      value={filter.symbol}
                      label="商品"
                      onChange={(e) => setFilter({ ...filter, symbol: e.target.value })}
                      sx={{ height: "40px" }}
                    >
                      <MenuItem value="">全部商品</MenuItem>
                      {SYMBOL_CODES.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* 風險篩選 */}
                  <FormControl size="small" sx={{
                    minWidth: 140,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                      "&:hover fieldset": { borderColor: "white" },
                    },
                    "& .MuiInputLabel-root": { color: "white" },
                    "& .MuiSvgIcon-root": { color: "white" }
                  }}>
                    <InputLabel sx={{ color: "white !important" }}>風險等級</InputLabel>
                    <Select
                      value={filter.riskLevel}
                      label="風險等級"
                      onChange={(e) => setFilter({ ...filter, riskLevel: e.target.value })}
                      sx={{ height: "40px" }}
                    >
                      <MenuItem value="">全部風險</MenuItem>
                      {Object.entries(RISK_LABELS).map(([k, v]) => (
                        <MenuItem key={k} value={k}>{v}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* 頻率篩選 */}
                  <FormControl size="small" sx={{
                    minWidth: 130,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                      "&:hover fieldset": { borderColor: "white" },
                    },
                    "& .MuiInputLabel-root": { color: "white" },
                    "& .MuiSvgIcon-root": { color: "white" }
                  }}>
                    <InputLabel sx={{ color: "white !important" }}>頻率</InputLabel>
                    <Select
                      value={filter.frequency}
                      label="頻率"
                      onChange={(e) => setFilter({ ...filter, frequency: e.target.value })}
                      sx={{ height: "40px" }}
                    >
                      <MenuItem value="">全部頻率</MenuItem>
                      {Object.entries(FREQ_LABELS).map(([k, v]) => (
                        <MenuItem key={k} value={k}>{v}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* 🔎 UUID 快速篩選（client-side 即時，輸入完整 UUID 或片段）*/}
                  <TextField
                    size="small"
                    placeholder="🔎 UUID 篩選"
                    value={uuidQuery}
                    onChange={(e) => setUuidQuery(e.target.value)}
                    sx={{
                      minWidth: 200,
                      "& .MuiOutlinedInput-root": {
                        color: "white", height: "40px",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                        "&:hover fieldset": { borderColor: "white" },
                      },
                      "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.8)", opacity: 1 },
                    }}
                    InputProps={{
                      endAdornment: uuidQuery ? (
                        <Icon sx={{ color: "white", cursor: "pointer" }} onClick={() => setUuidQuery("")}>close</Icon>
                      ) : null,
                    }}
                  />
                </MDBox>
              </MDBox>

              <MDBox pt={4} px={1} pb={2}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={8}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows: displayedElites }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 20, entries: [10, 20, 50, 100] }}
                    showTotalEntries={true}
                    noEndBorder
                    sx={{
                      "& th": {
                        fontSize: "0.85rem !important",
                        fontWeight: "800 !important",
                        textTransform: "uppercase",
                        color: "#344767 !important",
                        py: "12px !important"
                      },
                      "& td": {
                        fontSize: "0.8rem !important",
                        py: "16px !important"
                      }
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* 策略詳情彈窗 */}
      <Dialog open={!!selectedElite} onClose={() => setSelectedElite(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {selectedElite && (
          <>
            <DialogTitle sx={{ bgcolor: AUDIENCE_COLORS[selectedElite.audience] || "#757575", color: COLORS.white, fontWeight: 700 }}>
              {AUDIENCE_LABELS_FULL[selectedElite.audience] || selectedElite.audience} ·{" "}
              📄 {selectedElite.symbol} · {RISK_LABELS[selectedElite.riskLevel]} · {FREQ_LABELS[selectedElite.frequency]}
              {selectedElite.createdAt && (
                <MDTypography variant="caption" sx={{ display: "block", color: COLORS.white, opacity: 0.85, fontWeight: 400, mt: 0.25 }}>
                  入庫時間：{fmtDateTime(selectedElite.createdAt)}
                </MDTypography>
              )}
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              {/* 策略 UUID — 給管理員比對 DB 用 */}
              <MDBox px={2} py={1.5} display="flex" alignItems="center" gap={1.5} sx={{ bgcolor: COLORS.white, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="caption" fontWeight="bold" sx={{ color: COLORS.grayDark, minWidth: 80 }}>
                  策略 UUID
                </MDTypography>
                <MDTypography
                  variant="body2"
                  sx={{ fontFamily: "monospace", flex: 1, userSelect: "all", color: COLORS.black, fontSize: "0.85rem", wordBreak: "break-all" }}
                >
                  {selectedElite.id}
                </MDTypography>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() => {
                    navigator.clipboard?.writeText(selectedElite.id).catch(() => {});
                    setExeBuildSnackbar(`✓ 已複製 UUID：${selectedElite.id.slice(0, 8)}…`);
                  }}
                  sx={{ minWidth: "auto", px: 1.5, fontSize: "0.75rem" }}
                >
                  📋 複製
                </MDButton>
              </MDBox>
              <MDBox p={2} sx={{ bgcolor: COLORS.grayLightest, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>🤖 AI 策略摘要</MDTypography>
                <MDTypography variant="body2" sx={{ color: COLORS.grayDark, fontStyle: "italic", mb: 2 }}>
                  {selectedElite.aiSummary || "無摘要"}
                </MDTypography>

                {/* 審核理由改存 metadata（內部欄位、不外送給使用者）。
                    2026-08-07 前是直接蓋掉 aiSummary，庫裡仍有存量是那種內容。 */}
                {selectedElite.metadata?.lastAuditReason && (
                  <>
                    <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                      ⚖️ 最近一次審核結果
                      {selectedElite.lastAuditAt ? `（${fmtDateTime(selectedElite.lastAuditAt)}）` : ""}
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: COLORS.grayDark, mb: 2 }}>
                      {selectedElite.metadata.lastAuditReason}
                    </MDTypography>
                  </>
                )}

                <Grid container spacing={2}>
                  {[
                    { label: "年化回報", value: `${Number(selectedElite.returnRatePct).toFixed(2)}%` },
                    { label: "最大回撤", value: `${Number(selectedElite.maxDrawdownPct).toFixed(2)}%` },
                    { label: "獲利因子", value: Number(selectedElite.profitFactor).toFixed(2) },
                    { label: "Sharpe", value: Number(selectedElite.sharpeRatio).toFixed(2) },
                    { label: "交易次數", value: selectedElite.trades },
                    { label: "CAGR", value: `${Number(selectedElite.cagr).toFixed(2)}%` },
                  ].map((item) => (
                    <Grid item xs={4} key={item.label}>
                      <MDBox sx={{ textAlign: "center", p: 1, bgcolor: COLORS.white, borderRadius: 1, border: "1px solid #eee" }}>
                        <MDTypography variant="caption" fontWeight="bold">{item.label}</MDTypography>
                        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1a237e" }}>{item.value}</MDTypography>
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
              {/* 🚀 LIVE 狀態 + 代碼版本切換 */}
              <MDBox px={2} pt={1} pb={1.5} display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ borderBottom: "1px solid #eee" }}>
                {selectedElite.liveConvertedAt ? (
                  <MDBox sx={{ bgcolor: "#e8f5e9", color: COLORS.safe, px: 1.2, py: 0.4, borderRadius: 1, fontSize: "0.75rem", fontWeight: 700 }}>
                    ✅ LIVE Ready · 轉於 {new Date(selectedElite.liveConvertedAt).toLocaleString()}
                  </MDBox>
                ) : selectedElite.liveConvertReport ? (
                  <MDBox sx={{ bgcolor: "#ffebee", color: COLORS.error, px: 1.2, py: 0.4, borderRadius: 1, fontSize: "0.75rem", fontWeight: 700 }}>
                    ❌ LIVE 轉換失敗
                    {selectedElite.liveConvertReport.unclassified?.length > 0 && ` (${selectedElite.liveConvertReport.unclassified.length} 未分類)`}
                  </MDBox>
                ) : (
                  <MDBox sx={{ bgcolor: COLORS.grayLightest, color: "#455a64", px: 1.2, py: 0.4, borderRadius: 1, fontSize: "0.75rem", fontWeight: 700 }}>
                    ⚠️ 尚未轉 LIVE
                  </MDBox>
                )}

                <MDBox flex={1} />

                {/* 切換按鈕 */}
                <MDBox display="flex" sx={{ border: "1px solid #ddd", borderRadius: 1, overflow: "hidden" }}>
                  <Button
                    size="small"
                    onClick={() => setCodeView("live")}
                    disabled={!selectedElite.liveCode}
                    sx={{
                      bgcolor: codeView === "live" ? COLORS.safe : COLORS.white,
                      color: codeView === "live" ? COLORS.white : COLORS.grayDark,
                      borderRadius: 0,
                      "&:hover": { bgcolor: codeView === "live" ? "#1b5e20" : COLORS.grayLightest },
                      "&:disabled": { color: "#bbb" }
                    }}
                  >
                    🚀 LIVE 版本
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setCodeView("backtest")}
                    sx={{
                      bgcolor: codeView === "backtest" ? "#1a237e" : COLORS.white,
                      color: codeView === "backtest" ? COLORS.white : COLORS.grayDark,
                      borderRadius: 0,
                      "&:hover": { bgcolor: codeView === "backtest" ? "#0d1149" : COLORS.grayLightest }
                    }}
                  >
                    📜 Backtest
                  </Button>
                </MDBox>

                <Button
                  size="small" variant="outlined" color="success"
                  disabled={converting}
                  onClick={handleReconvertLive}
                  sx={{ fontWeight: 600 }}
                >
                  {converting ? "轉換中..." : "🔄 重轉 LIVE"}
                </Button>
              </MDBox>

              {/* 📦 EXE 打包區塊 */}
              {(() => {
                const exeInfo = exeBuildMap[selectedElite.id];
                // fallback：polling 的 exeBuildMap 可能還沒抓到這筆，要 fallback 到 selectedElite 本身的 row 資料
                // （表格列 line 478 已是這個模式；詳情漏了 fallback → status=DONE 也會被誤顯示成「未打包」）
                const exeStatus = exeInfo?.exeStatus ?? selectedElite.exeBuildStatus ?? "NEVER";
                const exeBuiltAt = exeInfo?.exeBuiltAt ?? selectedElite.exeBuiltAt;
                const exeBuildError = exeInfo?.exeBuildError ?? selectedElite.exeBuildError;
                const isOutdated = exeInfo?.isOutdated;
                const badge = {
                  DONE: isOutdated
                    ? { txt: "🕒 舊版本", bg: "#fff8e1", color: "#f57c00", desc: "目前 exe 版本過舊，建議重打" }
                    : { txt: "✅ Ready",  bg: "#e8f5e9", color: COLORS.safe, desc: `打包於 ${exeBuiltAt ? new Date(exeBuiltAt).toLocaleString() : ""}` },
                  RUNNING: { txt: "🔨 打包中", bg: "#e3f2fd", color: "#1565c0", desc: "PyInstaller 執行中..." },
                  PENDING: { txt: "⏳ 排隊",   bg: "#f3e5f5", color: "#6a1b9a", desc: "等待前一個打包完成" },
                  FAILED:  { txt: "❌ 失敗",   bg: "#ffebee", color: COLORS.error, desc: exeBuildError || "失敗" },
                  NEVER:   { txt: "⚪ 未打包", bg: COLORS.grayLightest, color: "#455a64", desc: "尚未產生執行檔" },
                }[exeStatus] || { txt: exeStatus, bg: COLORS.grayLightest, color: COLORS.black, desc: "" };
                const canBuild = !!selectedElite.liveCode && exeStatus !== "RUNNING" && exeStatus !== "PENDING";
                return (
                  <MDBox px={2} pb={1.5} display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ borderBottom: "1px solid #eee" }}>
                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#ef6c00" }}>📦 EXE 執行檔：</MDTypography>
                    <span style={{ background: badge.bg, color: badge.color, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 700 }}>
                      {badge.txt}
                    </span>
                    <span style={{ fontSize: "0.7rem", color: COLORS.grayDark }}>{badge.desc}</span>
                    <MDBox flex={1} />
                    {exeInfo?.exeBuildLog && (
                      <Button
                        size="small"
                        onClick={() => alert(exeInfo.exeBuildLog)}
                        sx={{ fontSize: "0.7rem" }}
                      >
                        📋 看 log
                      </Button>
                    )}
                    {canBuild && (
                      <Button
                        size="small" variant="outlined"
                        color={exeStatus === "FAILED" ? "error" : "warning"}
                        disabled={exeBuildBusy}
                        onClick={() => handleBuildOneExe(selectedElite.id)}
                        sx={{ fontWeight: 600 }}
                      >
                        {exeStatus === "DONE" ? "🔁 重新打包" : "📦 打包 EXE"}
                      </Button>
                    )}
                  </MDBox>
                );
              })()}

              {/* 📋 Log viewer 區（展開時顯示）*/}
              {exeBuildMap[selectedElite.id]?.exeBuildLog && (
                <details style={{ margin: "8px 16px" }}>
                  <summary style={{ cursor: "pointer", fontSize: "0.75rem", color: COLORS.grayDark, padding: "4px 0" }}>
                    📋 PyInstaller 打包 log（點開展開）
                  </summary>
                  <MDBox sx={{
                    bgcolor: "#1e1e2e", color: "#cdd6f4", p: 1.5, borderRadius: 1, mt: 1,
                    fontFamily: "monospace", fontSize: "0.7rem", whiteSpace: "pre-wrap",
                    maxHeight: 300, overflow: "auto"
                  }}>
                    {exeBuildMap[selectedElite.id].exeBuildLog}
                  </MDBox>
                </details>
              )}

              <MDBox
                component="pre"
                sx={{
                  m: 0, p: 2.5,
                  bgcolor: codeView === "live" ? "#0d3b1e" : "#1e1e2e",
                  color: codeView === "live" ? "#d4f5dc" : "#cdd6f4",
                  fontFamily: "monospace", fontSize: "0.75rem", overflow: "auto", maxHeight: 500,
                  whiteSpace: "pre-wrap", wordBreak: "break-all"
                }}
              >
                {codeView === "live" && selectedElite.liveCode
                  ? selectedElite.liveCode
                  : selectedElite.code}
              </MDBox>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedElite(null)} color="primary">關閉</Button>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  const txt = (codeView === "live" && selectedElite.liveCode) ? selectedElite.liveCode : selectedElite.code;
                  navigator.clipboard.writeText(txt);
                  alert(`${codeView === "live" ? "LIVE" : "Backtest"} 代碼已複製到剪貼簿`);
                }}
              >
                複製{codeView === "live" ? " LIVE " : " Backtest "}代碼
              </MDButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

export default BankElitePage;
